import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/schema';
import { useHistory } from 'react-router-dom';
import { setCurrentSchemaDetails } from 'store/schema/actions';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import { ToolbarV2, TableComponentV2 } from 'components';
import { schemaTableTitles } from 'constants.js';
import filterHelpers from 'store/helpers/filterHelpers';
import { updateSchemaFilters, resetSchemaFilters } from 'store/schema/actions';
import { NearMeOutlined } from '@material-ui/icons';

const {
  getPaginatedSchemas,
  unsetCurrentSchema,
  setCurrentSchema,
  unsetCurrentSchemaDetails,
  activateOrDeactivateSchema,
  getEventTypes
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const Schema = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    onClickToggleArchive
  } = filterHelpers();
  const action = getPaginatedSchemas({ force: true });
  const schemas = useSelector(store => store.schema.paginatedList);
  const isLoading = useSelector(store => store.schema.gettingPaginated);
  const activateOrDeactivateSucceeded = useSelector(
    store => store.schema.activateOrDeactivateSucceeded
  );
  const activateOrDeactivateFailed = useSelector(
    store => store.schema.activateOrDeactivateFailed
  );
  const activateOrDeactivateFailedMessage = useSelector(
    store => store.schema.activateOrDeactivateFailedMessage
  );

  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.productCategories
  );
  const filters = useSelector(store => store.schema.filters);

  const optionsToFilterProductNames = productNames
    ? productNames.map(productName => {
        return { name: 'productName', value: productName, checked: false };
      })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map(productCategory => {
        return {
          name: 'productCategory',
          value: productCategory,
          checked: false
        };
      })
    : [];

  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    dispatch(getPaginatedSchemas({ force: true }))
    dispatch(getEventTypes());
  }, []);

  useEffect(() => {
    if (activateOrDeactivateSucceeded) {
      dispatch(getPaginatedSchemas({ force: true }));
    }
  }, [activateOrDeactivateSucceeded]);

  useEffect(() => {
    setOpenSnackbar(activateOrDeactivateFailed);
  }, [activateOrDeactivateFailed]);

  const onAddSchema = () => {
    dispatch(unsetCurrentSchema());
    dispatch(unsetCurrentSchemaDetails());
  };

  const onEditSchema = schema => {
    dispatch(unsetCurrentSchema());
    dispatch(setCurrentSchema(schema));
  };

  const onCopySchema = id => {
    let copiedSchema = schemas.filter(schema => schema.id === id)[0];
    dispatch(
      setCurrentSchemaDetails({
        name: copiedSchema.schemaName + '-copy',
        option:
          copiedSchema.eventProductCategory +
          ' - ' +
          copiedSchema.eventProductName +
          ' - ' +
          copiedSchema.eventTypeCategory,
        isDefaultSchema: copiedSchema.eventTypeDefault,
        hasMovedToNextStep: true
      })
    );
    dispatch(
      setCurrentSchema({
        ...copiedSchema,
        schemaName: copiedSchema.schemaName + '-copy',
        id: ''
      })
    );
    history.push('/schema/details');
  };

  const onArchiveOrActivateSchema = id => {
    let action = activateOrDeactivateSchema({
      schemaId: id,
      activeStatus: filters?.displayActive
    });
    dispatch(action);
  };

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateSchemaFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateSchemaFilters({ filterProductNames: productNames, pageNo: 0 }))
      }
    ],
    resetFilters: () => dispatch(resetSchemaFilters())
  };

  const onEdit = row => {
    onEditSchema(row);
    history.push(`/schema/design/${row.id}`);
  }
  const onCopy = id => {
    onCopySchema(id);
  }
  const onClickArchive = id => {
    onArchiveOrActivateSchema(id);
  };
  const onClickAddSchema = () => {
    onAddSchema();
    history.push(`/schema/details`);
  };
  const getButtonsGroup = () => [
    {
      name: `Enriched Schema configuration`,
      action: () => history.push('/enriched-schema'),
      startIcon: <NearMeOutlined fontSize="default" />
    },
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Schemas`,
      action: () => onClickToggleArchive(action, updateSchemaFilters, { displayActive: !filters?.displayActive, pageNo: 0 }),
    },
    {
      name: 'Add Schema',
      action: () => onClickAddSchema()
    }
  ];

  const getActionButtons = schema => [
    {
      name: 'Edit Schema',
      action: ['ACTION', () => onEdit(schema)]
    },
    {
      name: 'Copy Schema',
      action: ['ACTION', () => onCopy(schema.id)]
    },
    {
      name: filters?.displayActive ? 'Archive Schema' : 'Activate Schema',
      action: ['ACTION', () => onClickArchive(schema.id)]
    }
  ];
  
  const searchOptions = {
    searchPlaceholder: 'Search Schemas',
    searchAction: (payload) => dispatch(updateSchemaFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedSchemas({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={schemaTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={schemas}
          action={getPaginatedSchemas({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateSchemaFilters(payload))}
        />
      </div>
      <UserMessageSnackbar
        openSnackbar={openSnackbar && activateOrDeactivateFailedMessage !== ''}
        message={activateOrDeactivateFailedMessage}
        severity="error"></UserMessageSnackbar>
    </div>
  );
};

export default Schema;
